/**
 * Motyw jasny/ciemny: jedno miejsce zamiast trzech.
 *
 * Usterka, ktora to wywolala (zgloszona 2026-08-11): po wlaczeniu trybu ciemnego i odswiezeniu
 * strony aplikacja wracala do jasnego, a wlaczala sie dopiero po wejsciu w Ustawienia. Przyczyna
 * nie byla w zapisie, tylko w tym, ze przy starcie NIKT motywu nie nakladal:
 *
 *   - `App.js` mial komplet funkcji (`setThemeMode`, `initThemeFromStorage`, `loadUserPreferences`),
 *     ale `loadUserPreferences` nie bylo wolane ANI RAZU w calym kodzie, a `initThemeFromStorage`
 *     tylko w jego bloku obslugi bledu — czyli rowniez nigdy;
 *   - `Preferences.js` mial WLASNA kopie nakladania motywu i odpalal ja przy wejsciu na strone.
 *
 * Stad dokladnie to, co widac: po odswiezeniu jasny, po wejsciu w ustawienia nagle ciemny.
 *
 * Do tego obie kopie uzywaly INNEGO KLUCZA w localStorage: Ustawienia zapisywaly pod `theme`,
 * a martwy kod w App.js czytal `themeMode`. Nawet gdyby ktos go wywolal, nie zobaczylby zapisu.
 * Zrodlem prawdy jest `theme`, bo to jego pisze ekran Ustawien; `themeMode` czytamy jeszcze
 * awaryjnie, zeby nikomu nie zgubic wyboru.
 *
 * Modul jest czysty (przyjmuje `okno` i `dokument`), zeby dalo sie go przetestowac bez przegladarki.
 */

export const TRYBY = ['light', 'dark', 'system'];

/** Nieznana wartosc ma dawac jasny, a nie wywalac aplikacje ani zostawiac jej bez motywu. */
export function normalizujTryb(tryb) {
  return TRYBY.includes(tryb) ? tryb : 'light';
}

export function trybSystemowy(okno) {
  const w = okno || (typeof window !== 'undefined' ? window : null);
  if (!w || typeof w.matchMedia !== 'function') return 'light';
  try {
    return w.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  } catch (e) {
    return 'light';
  }
}

/** Co ma faktycznie trafic do atrybutu `data-theme` dla danego trybu. */
export function motywDoNalozenia(tryb, okno) {
  const t = normalizujTryb(tryb);
  return t === 'system' ? trybSystemowy(okno) : t;
}

export function odczytajTryb(okno) {
  const w = okno || (typeof window !== 'undefined' ? window : null);
  try {
    if (!w || !w.localStorage) return 'light';
    return normalizujTryb(w.localStorage.getItem('theme') || w.localStorage.getItem('themeMode'));
  } catch (e) {
    return 'light';
  }
}

function stosuj(motyw, dokument) {
  const d = dokument || (typeof document !== 'undefined' ? document : null);
  if (d && d.documentElement) d.documentElement.setAttribute('data-theme', motyw);
}

/**
 * Ustawia tryb: zapisuje, naklada i pilnuje trybu systemowego.
 *
 * Nasluch zmiany schematu systemowego trzymamy na `window`, zeby przelaczenie na `light`/`dark`
 * go zdejmowalo. Bez tego kazde kolejne wejscie w Ustawienia dokladalo nowy i po kilku zmianach
 * motyw zaczynal skakac przy zmianie ustawien systemu.
 */
export function ustawTryb(tryb, okno, dokument) {
  const w = okno || (typeof window !== 'undefined' ? window : null);
  const t = normalizujTryb(tryb);

  try {
    if (w && w.localStorage) w.localStorage.setItem('theme', t);
  } catch (e) { /* tryb prywatny: motyw i tak nalozymy, tylko go nie zapamietamy */ }

  if (w && w.__themeMql) {
    const { mql, handler } = w.__themeMql;
    if (mql.removeEventListener) mql.removeEventListener('change', handler);
    else if (mql.removeListener) mql.removeListener(handler);
    w.__themeMql = null;
  }

  stosuj(motywDoNalozenia(t, w), dokument);

  if (t === 'system' && w && typeof w.matchMedia === 'function') {
    // Osloniete tak samo jak odczyt wyzej: ta funkcja biegnie w index.js PRZED renderem, wiec
    // wyjatek nie zostalby przez nic zlapany i zamiast zdegradowac sie do jasnego motywu
    // wygasilby cala aplikacje.
    try {
      const mql = w.matchMedia('(prefers-color-scheme: dark)');
      const handler = () => stosuj(trybSystemowy(w), dokument);
      if (mql.addEventListener) mql.addEventListener('change', handler);
      else if (mql.addListener) mql.addListener(handler);
      w.__themeMql = { mql, handler };
    } catch (e) { /* zostaje motyw juz nalozony wyzej */ }
  }

  return t;
}

/** Wolane RAZ przy starcie aplikacji, zanim cokolwiek sie wyrenderuje. */
export function nalozZapisanyMotyw(okno, dokument) {
  return ustawTryb(odczytajTryb(okno), okno, dokument);
}
